import CloudQueueRounded from '@mui/icons-material/CloudQueueRounded'
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined'
import LightModeOutlined from '@mui/icons-material/LightModeOutlined'
import WorkspacesRounded from '@mui/icons-material/WorkspacesRounded'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import type { VulcanAppDefinition } from '../../configuration/apps'
import { Brand, Sidebar } from './VulcanNavigation.styles'

export function VulcanNavigation({
  apps,
  activeAppId,
  mode,
  email,
  onNavigate,
  onToggleColorMode,
  onSignOut
}: {
  apps: readonly VulcanAppDefinition[]
  activeAppId: string
  mode: 'light' | 'dark'
  email?: string
  onNavigate: (app: VulcanAppDefinition) => void
  onToggleColorMode: () => void
  onSignOut: () => Promise<void>
}) {
  const darkMode = mode === 'dark'
  return (
    <Sidebar>
      <Brand>
        <CloudQueueRounded sx={{ color: '#a395ed', fontSize: 30 }} />
        <Typography
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.03em',
            fontSize: '1.2rem',
            '@media (max-width: 760px)': { display: 'none' }
          }}
        >
          Vulcan
        </Typography>
      </Brand>
      <Typography
        color="rgba(255,255,255,0.56)"
        sx={{
          fontSize: '0.7rem',
          letterSpacing: '0.08em',
          mt: 3,
          px: 1,
          textTransform: 'uppercase',
          '@media (max-width: 760px)': { display: 'none' }
        }}
      >
        Applications
      </Typography>
      <List component="nav" sx={{ mt: 0.75, px: 0 }}>
        {apps.map((app) => {
          const selected = app.id === activeAppId
          const Icon = app.icon === 'cloud' ? CloudQueueRounded : WorkspacesRounded
          return (
            <ListItemButton
              key={app.id}
              aria-current={selected ? 'page' : undefined}
              selected={selected}
              sx={{
                borderRadius: 1.5,
                color: 'rgba(255,255,255,0.8)',
                mb: 0.5,
                '&.Mui-selected': { bgcolor: 'rgba(163,149,237,0.22)', color: '#fff' },
                '&.Mui-selected:hover': { bgcolor: 'rgba(163,149,237,0.3)' },
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                '@media (max-width: 760px)': { justifyContent: 'center', px: 1 }
              }}
              onClick={() => onNavigate(app)}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 36 }}>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText
                primary={app.label}
                secondary={app.description}
                slotProps={{
                  primary: { noWrap: true, sx: { fontWeight: selected ? 700 : 500 } },
                  secondary: { noWrap: true, sx: { color: 'rgba(255,255,255,0.52)', fontSize: '0.7rem' } }
                }}
                sx={{ '@media (max-width: 760px)': { display: 'none' } }}
              />
            </ListItemButton>
          )
        })}
      </List>
      <Box sx={{ flex: 1 }} />
      <Stack sx={{ gap: 1, px: 0.5 }}>
        <Typography
          color="rgba(255,255,255,0.58)"
          sx={{
            fontSize: '0.7rem',
            overflow: 'hidden',
            px: 0.5,
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            '@media (max-width: 760px)': { display: 'none' }
          }}
        >
          {email ?? 'Signed in'}
        </Typography>
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <IconButton
            aria-label={`Switch to ${darkMode ? 'light' : 'dark'} mode`}
            color="inherit"
            onClick={onToggleColorMode}
            size="small"
          >
            {darkMode ? <LightModeOutlined fontSize="small" /> : <DarkModeOutlined fontSize="small" />}
          </IconButton>
          <IconButton aria-label="Sign out" color="inherit" onClick={() => void onSignOut()} size="small">
            <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700 }}>
              Exit
            </Typography>
          </IconButton>
        </Stack>
      </Stack>
    </Sidebar>
  )
}
